import React from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { getDataPoint } from './fields';

export const DropdownIndicator = () => (
  <div className="d-flex align-items-center">
    <img style={{ paddingRight: '0.5rem' }} src="/manage/images/arrow.svg" alt="" />
  </div>
);

export const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '40px',
    borderRadius: '4px',
    borderColor: state.isFocused ? '#753cb7' : '#e6e6e6',
    boxShadow: 'none',
    ':hover': { borderColor: '#753cb7' },
  }),
  valueContainer: (provided) => ({ ...provided, padding: '2px 8px', gap: '4px' }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#ffffff',
    border: '1px solid #e6e6e6',
    borderRadius: '40px',
    padding: '1px 8px',
    margin: '2px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontWeight: 500,
    fontSize: '14px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#9ca3af',
    ':hover': { backgroundColor: 'transparent', color: '#000000' },
  }),
  placeholder: (provided) => ({ ...provided, color: '#9ca3af', fontSize: '14px' }),
  singleValue: (provided) => ({ ...provided, fontSize: '14px', color: '#1e293b' }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '14px',
    backgroundColor: state.isSelected ? '#753cb7' : state.isFocused ? '#f4eefb' : '#ffffff',
    color: state.isSelected ? '#ffffff' : '#1e293b',
  }),
  menu: (provided) => ({ ...provided, zIndex: 20 }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

/**
 * The panel scrolls once enough criteria are added, so menus are portalled to
 * the body to stop them being clipped by the panel's own overflow.
 */
export const commonSelectProps = {
  components: { DropdownIndicator },
  styles: selectStyles,
  classNamePrefix: 'advanced-search-select',
  menuPortalTarget: typeof document === 'undefined' ? null : document.body,
  menuPosition: 'fixed',
};

const BOOLEAN_OPTIONS = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

/**
 * Renders the value control for one advanced-search row. The control is chosen
 * from the data point's `valueType`, so adding a data point in `fields.js` is
 * enough to get the right input here.
 */
const ValueField = ({ field, value, onChange, optionSources = {} }) => {
  const dataPoint = getDataPoint(field);

  if (!dataPoint) {
    return (
      <input
        className="lg-input Custom-input advanced-search__input"
        placeholder="Select a data point first"
        disabled
        value=""
        readOnly
      />
    );
  }

  const source = optionSources[dataPoint.optionsKey] || {};
  const options = source.options || [];

  switch (dataPoint.valueType) {
    case 'multi': {
      const Component = dataPoint.isCreatable ? CreatableSelect : Select;

      if (source.loadOptions) {
        return (
          <AsyncSelect
            isMulti
            cacheOptions
            // `true` makes the select fetch its own list when it mounts, so the
            // options are whatever the API returns right now.
            defaultOptions
            loadOptions={source.loadOptions}
            formatOptionLabel={source.formatOptionLabel}
            {...commonSelectProps}
            value={value || []}
            onChange={(selected) => onChange(selected || [])}
            placeholder="Select value"
          />
        );
      }

      return (
        <Component
          isMulti
          options={options}
          {...commonSelectProps}
          value={value || []}
          onChange={(selected) => onChange(selected || [])}
          placeholder="Select value"
        />
      );
    }

    case 'options': {
      if (source.loadOptions) {
        return (
          <AsyncSelect
            cacheOptions
            // `true` makes the select fetch its own list when it mounts, so the
            // options are whatever the API returns right now.
            defaultOptions
            loadOptions={source.loadOptions}
            formatOptionLabel={source.formatOptionLabel}
            {...commonSelectProps}
            value={value || null}
            onChange={(selected) => onChange(selected)}
            placeholder="Select value"
            isClearable
          />
        );
      }

      const Component = dataPoint.isCreatable ? CreatableSelect : Select;
      return (
        <Component
          options={options}
          {...commonSelectProps}
          value={value || null}
          onChange={(selected) => onChange(selected)}
          placeholder="Select value"
          isClearable
        />
      );
    }

    case 'boolean':
      return (
        <Select
          options={BOOLEAN_OPTIONS}
          {...commonSelectProps}
          value={
            typeof value?.value === 'boolean'
              ? value
              : BOOLEAN_OPTIONS.find((option) => option.value === value) || null
          }
          onChange={(selected) => onChange(selected)}
          placeholder="Select value"
        />
      );

    case 'date':
      return (
        <input
          type="date"
          className="lg-input Custom-input advanced-search__input"
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          min="0"
          className="lg-input Custom-input advanced-search__input"
          placeholder="Enter value"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    default:
      return (
        <input
          type="text"
          className="lg-input Custom-input advanced-search__input"
          placeholder="Enter value"
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
};

export default ValueField;
